/**
 * Работа с ФИО.
 *
 * В исходной таблице ФИО лежит одной строкой («Мурашко Дмитрий Владимирович»).
 * Мы храним и строку целиком (её видит рекрутёр и по ней ищет),
 * и разбор на три части — он нужен для сортировки по фамилии,
 * для дедупликации и для подстановки в печатные формы.
 */

export interface ParsedName {
  lastName: string | null;
  firstName: string | null;
  middleName: string | null;
}

/**
 * Разбирает ФИО в порядке «Фамилия Имя Отчество» — так пишут в таблице.
 * Двойные фамилии через дефис («Петров-Водкин») остаются целыми.
 */
export function parseFullName(fullName: string | null | undefined): ParsedName {
  if (!fullName) return { lastName: null, firstName: null, middleName: null };

  const parts = fullName
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);

  return {
    lastName: parts[0] ?? null,
    firstName: parts[1] ?? null,
    // Всё, что после имени, считаем отчеством: встречаются составные
    middleName: parts.length > 2 ? parts.slice(2).join(' ') : null,
  };
}

export function buildFullName(p: ParsedName): string {
  return [p.lastName, p.firstName, p.middleName].filter(Boolean).join(' ');
}

/** Инициалы для аватара пользователя и метки ответственного в гриде. */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${(parts[0] ?? '')[0] ?? ''}${(parts[1] ?? '')[0] ?? ''}`.toUpperCase();
}

/**
 * Ключ для поиска дублей по ФИО: нижний регистр, ё→е, схлопнутые пробелы.
 * «Ёлкина  Анна» и «елкина анна» дают одинаковый ключ.
 */
export function nameKey(fullName: string | null | undefined): string {
  if (!fullName) return '';
  return fullName
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^а-яa-z\s-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Общие признаки для PostgreSQL и резервного расчёта пола в отчётах. */
export const FEMALE_PATRONYMIC_PATTERN = '(овна|евна|ична)$|(^|[[:space:]-])кызы$';
export const MALE_PATRONYMIC_PATTERN = '(ович|евич)$|(^|[[:space:]-])(ильич|кузьмич|лукич|фомич|саввич|никитич|оглы)$';
const femalePatronymic = new RegExp(FEMALE_PATRONYMIC_PATTERN.replaceAll('[[:space:]-]', '[\\s-]'));
const malePatronymic = new RegExp(MALE_PATRONYMIC_PATTERN.replaceAll('[[:space:]-]', '[\\s-]'));

/** Не записывает предположение в карточку; заполненное значение всегда важнее. */
export function reportGender(gender: string | null | undefined, middleName: string | null | undefined): 'M' | 'F' | 'UNKNOWN' {
  if (gender === 'M' || gender === 'F') return gender;
  const name = middleName?.trim().toLowerCase() ?? '';
  if (femalePatronymic.test(name)) return 'F';
  if (malePatronymic.test(name)) return 'M';
  return 'UNKNOWN';
}
